import React from "react";
import {
    Package,
    Image,
    CheckCircle2,
    DollarSign,
    CalendarDays,
    Star,
    Layers,
    Edit3,
    Eye,
    EyeOff,
    CircleSlash,
    Trash2,
} from "lucide-react";

export default function PackageList({ packages, onEdit, onToggleVisibility, onDelete, onDisable }) {
    return (
        <div className="max-w-6xl mx-auto p-6">
            <h2 className="text-2xl font-semibold text-gray-800 flex items-center gap-2 mb-8">
                <Layers className="w-6 h-6 text-green-600" /> Available Packages
            </h2>

            {packages.length === 0 ? (
                <p className="text-gray-500 text-center mt-10">No packages found.</p>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {packages.map((pkg) => (
                        <div
                            key={pkg._id}
                            className="bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 p-5 flex flex-col justify-between"
                        >
                            {/* Header */}
                            <div>
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-2">
                                        <Package className="w-5 h-5 text-green-600" />
                                        <h3 className="text-lg font-semibold text-gray-800">{pkg.name}</h3>
                                    </div>

                                    {/* Status Badge */}
                                    {pkg.disabled ? (
                                        <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-md font-medium">
                                            Disabled
                                        </span>
                                    ) : pkg.hidden ? (
                                        <span className="text-xs bg-yellow-100 text-yellow-600 px-2 py-0.5 rounded-md font-medium">
                                            Hidden
                                        </span>
                                    ) : (
                                        <span className="text-xs bg-green-100 text-green-600 px-2 py-0.5 rounded-md font-medium">
                                            Active
                                        </span>
                                    )}
                                </div>

                                {/* ⭐ Star Rating */}
                                {pkg.star !== undefined && (
                                    <div className="flex items-center gap-1 mb-3">
                                        <span className="text-sm font-medium text-gray-700">Star:</span>

                                        {/* star icons */}
                                        <div className="flex gap-0.5">
                                            {Array.from({ length: pkg.star }).map((_, i) => (
                                                <Star
                                                    key={i}
                                                    className={`w-4 h-4 ${i < pkg.star
                                                        ? "text-yellow-500 fill-yellow-500"
                                                        : "text-gray-300"
                                                        }`}
                                                />
                                            ))}
                                        </div>

                                        {/* <span className="text-xs text-gray-500 ml-1">({pkg.star})</span> */}
                                    </div>
                                )}

                                {/* Limits */}
                                <div className="flex items-center gap-3 text-sm text-gray-600 mb-4">
                                    <div className="flex items-center gap-1">
                                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                                        <span>{pkg.dailyPostLimit} Posts/day</span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <Image className="w-4 h-4 text-blue-500" />
                                        <span>{pkg.dailyPhotoLimit} Photos/day</span>
                                    </div>
                                </div>

                                {/* Features */}
                                {pkg.features.length > 0 && (
                                    <div className="border-t border-gray-100 pt-3 mb-3">
                                        <p className="text-sm font-medium text-gray-700 flex items-center gap-1 mb-2">
                                            <Star className="w-4 h-4 text-yellow-500" /> Features
                                        </p>
                                        <ul className="text-sm text-gray-600 space-y-1">
                                            {pkg.features.map((feature, i) => (
                                                <li key={i} className="flex items-center gap-1">
                                                    <CheckCircle2 className="w-3 h-3 text-green-500" />
                                                    {feature}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {/* Plans */}
                                <div className="border-t border-gray-100 pt-3">
                                    <p className="text-sm font-medium text-gray-700 flex items-center gap-1 mb-2">
                                        <DollarSign className="w-4 h-4 text-green-500" /> Plans
                                    </p>

                                    <div className="space-y-2">
                                        {pkg.plans.map((plan, i) => (
                                            <div
                                                key={i}
                                                className="flex justify-between items-center text-sm bg-gray-50 rounded-lg px-3 py-2 border border-gray-100"
                                            >
                                                <span className="font-medium text-gray-700">{plan.name}</span>

                                                <span className="text-gray-600 flex items-center gap-1">
                                                    ₹{plan.price}
                                                    <span className="text-xs text-gray-400 flex items-center gap-1">
                                                        <CalendarDays className="w-3 h-3 text-gray-400" />
                                                        {plan.days} days
                                                    </span>
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Footer (Actions) */}
                            <div className="pt-4 mt-5 border-t border-gray-100 flex justify-between items-center">
                                <button
                                    onClick={() => onEdit(pkg)}
                                    className="flex items-center gap-1 text-blue-600 hover:text-blue-800 text-sm font-medium"
                                >
                                    <Edit3 className="w-4 h-4" /> Edit
                                </button>

                                <button
                                    onClick={() => onToggleVisibility(pkg)}
                                    className={`flex items-center gap-1 text-sm font-medium ${pkg.hidden
                                        ? "text-green-600 hover:text-green-800"
                                        : "text-yellow-600 hover:text-yellow-800"
                                        }`}
                                >
                                    {pkg.hidden ? (
                                        <>
                                            <Eye className="w-4 h-4" /> Unhide
                                        </>
                                    ) : (
                                        <>
                                            <EyeOff className="w-4 h-4" /> Hide
                                        </>
                                    )}
                                </button>

                                {onDelete && (
                                    <button
                                        onClick={() => onDelete(pkg)}
                                        className="flex items-center gap-1 text-sm font-medium text-red-600 hover:text-red-700"
                                        title="Soft-delete (recoverable from Archives within last 20)"
                                    >
                                        <Trash2 className="w-4 h-4" /> Delete
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
